import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './PaymentPage.css';

const PaymentPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loanDetails, setLoanDetails] = useState(null);
    const [loadingLoan, setLoadingLoan] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [formData, setFormData] = useState({
        amount: '',
        loanId: location.state?.loanId || '',
        purpose: location.state?.loanId ? 'loan' : 'general'
    });

    const API_URL = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        // Get user from localStorage (checking both keys for compatibility)
        const userData = localStorage.getItem('userSession') || localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        } else {
            console.warn('⚠️ [PAYMENT] No user session found, redirecting to login');
            navigate('/login');
        }
    }, [navigate]);

    const lateFee = loanDetails?.lateFee || 0;
    const lateFeeAmount = loanDetails?.lateFeeAmount || 750;
    const totalDue = loanDetails?.totalDue || loanDetails?.emiAmount || 0;
    const cutoffDay = loanDetails?.cutoffDay || 5;

    // Fetch loan EMI details when loanId changes
    useEffect(() => {
        const fetchLoanEMI = async (loanId) => {
            const effectiveUserId = user?.id || user?.userId;

            if (!user || !effectiveUserId) {
                console.warn('⚠️ [PAYMENT] User ID missing, skipping fetch. User object:', JSON.stringify(user));
                return;
            }

            console.log(`📊 [PAYMENT] Fetching EMI for Loan ${loanId}, User ${effectiveUserId}`);

            setLoadingLoan(true);
            setValidationError('');

            try {
                const response = await fetch(
                    `${API_URL}/api/payment/loan-emi/${loanId}?userId=${effectiveUserId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );

                const data = await response.json();

                if (data.success && data.canPay) {
                    setLoanDetails(data.loanDetails);
                    setFormData(prev => ({
                        ...prev,
                        amount: (data.loanDetails.totalDue ?? data.loanDetails.emiAmount).toString()
                    }));
                    setValidationError('');
                } else {
                    setValidationError(data.message);
                    setLoanDetails(null);
                    setFormData(prev => ({
                        ...prev,
                        amount: ''
                    }));
                }
            } catch (error) {
                console.error('Error fetching loan EMI:', error);
                setValidationError('Failed to fetch loan details. Please try again.');
                setLoanDetails(null);
            } finally {
                setLoadingLoan(false);
            }
        };

        if (formData.loanId && user) {
            fetchLoanEMI(formData.loanId);
        } else {
            setLoanDetails(null);
            setValidationError('');
        }
    }, [formData.loanId, user, API_URL]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear loan details if purpose changes from loan
        if (name === 'purpose' && value !== 'loan') {
            setLoanDetails(null);
            setValidationError('');
            setFormData(prev => ({
                ...prev,
                loanId: '',
                amount: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log('🚀 [PAYMENT] Form submitted');
        console.log('📦 [PAYMENT] Form data:', formData);

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            alert('Please enter a valid amount');
            return;
        }

        setLoading(true);

        try {
            const effectiveUserId = user?.id || user?.userId;
            // Robust user data extraction to prevent crashes
            const firstName = user.first_name || (user.name ? user.name.split(' ')[0] : 'Customer');
            const phone = user.phone_number || user.phone || '9999999999';
            const email = user.email || 'customer@example.com';

            const paymentRequest = {
                userId: effectiveUserId,
                loanId: formData.loanId || null,
                amount: formData.amount,
                firstname: firstName,
                email: email,
                phone: phone
            };

            console.log('📤 [PAYMENT] Sending request to backend:', paymentRequest);

            const response = await fetch(`${API_URL}/api/payment/initiate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(paymentRequest)
            });

            console.log('📥 [PAYMENT] Response status:', response.status);

            const data = await response.json();
            console.log('📥 [PAYMENT] Response data:', data);

            if (data.success) {
                console.log('✅ [PAYMENT] Payment data received, creating form...');
                console.log('🌐 [PAYMENT] PayU URL:', data.paymentData.payuUrl);

                // Create a form and submit to PayU
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = data.paymentData.payuUrl;

                // Add all payment data as hidden fields
                Object.keys(data.paymentData).forEach(key => {
                    if (key !== 'payuUrl') {
                        const input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = key;
                        input.value = data.paymentData[key];
                        form.appendChild(input);
                        console.log(`➕ [PAYMENT] Added field: ${key} = ${input.value.substring(0, 50)}...`);
                    }
                });

                document.body.appendChild(form);
                console.log('📝 [PAYMENT] Form created and appended to body');
                console.log('🚀 [PAYMENT] Submitting form to PayU...');
                form.submit();
            } else {
                console.error('❌ [PAYMENT] Payment initialization failed:', data.message);
                alert('Payment initialization failed: ' + data.message);
                setLoading(false);
            }
        } catch (error) {
            console.error('❌ [PAYMENT] Error:', error);
            alert('Failed to initiate payment. Please try again. Error: ' + error.message);
            setLoading(false);
        }
    };

    if (!user) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="payment-page">
            <div className="payment-container">
                <div className="payment-header">
                    <div className="payment-icon">💳</div>
                    <h1>Make a Payment</h1>
                    <p>Secure payment powered by PayU</p>
                </div>

                <form onSubmit={handleSubmit} className="payment-form">
                    <div className="form-group">
                        <label htmlFor="purpose">Payment Purpose</label>
                        <select
                            id="purpose"
                            name="purpose"
                            value={formData.purpose}
                            onChange={handleInputChange}
                            className="form-control"
                        >
                            <option value="general">General Payment</option>
                            <option value="loan">Loan EMI Payment</option>
                        </select>
                    </div>

                    {formData.purpose === 'loan' && (
                        <div className="form-group">
                            <label htmlFor="loanId">Loan ID *</label>
                            <input
                                type="text"
                                id="loanId"
                                name="loanId"
                                value={formData.loanId}
                                onChange={handleInputChange}
                                placeholder="Enter your loan ID"
                                className="form-control"
                                required
                                readOnly={!!location.state?.loanId}
                                style={{
                                    backgroundColor: location.state?.loanId ? '#f3f4f6' : 'white',
                                    cursor: location.state?.loanId ? 'not-allowed' : 'text'
                                }}
                            />
                            {location.state?.loanId && (
                                <small style={{ color: '#6b7280', display: 'block', marginTop: '0.25rem' }}>
                                    🔒 Locked to selected loan
                                </small>
                            )}
                            {loadingLoan && (
                                <div style={{ marginTop: '0.5rem', color: '#667eea', fontSize: '0.875rem' }}>
                                    <span className="spinner" style={{ width: '14px', height: '14px', marginRight: '0.5rem' }}></span>
                                    Fetching loan details...
                                </div>
                            )}
                        </div>
                    )}

                    {/* Validation Error Display */}
                    {validationError && (
                        <div className="error-box">
                            <span className="error-icon">⚠️</span>
                            <div>
                                <strong>Payment Not Allowed</strong>
                                <p>{validationError}</p>
                            </div>
                        </div>
                    )}

                    {/* Loan EMI Details Display */}
                    {loanDetails && (
                        <div className="loan-emi-details">
                            <h3>📊 Loan EMI Breakdown</h3>
                            <div className="emi-grid">
                                <div className="emi-item">
                                    <span className="emi-label">Loan Type</span>
                                    <span className="emi-value">{loanDetails.loanType}</span>
                                </div>
                                <div className="emi-item">
                                    <span className="emi-label">Account Number</span>
                                    <span className="emi-value">{loanDetails.accountNumber}</span>
                                </div>
                                <div className="emi-item">
                                    <span className="emi-label">Principal Amount</span>
                                    <span className="emi-value">₹{loanDetails.principalAmount.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="emi-item">
                                    <span className="emi-label">Interest Rate</span>
                                    <span className="emi-value">{loanDetails.interestRate}% p.a.</span>
                                </div>
                                <div className="emi-item">
                                    <span className="emi-label">Tenure</span>
                                    <span className="emi-value">{loanDetails.tenureMonths} months</span>
                                </div>
                                <div className="emi-item highlight">
                                    <span className="emi-label">Monthly EMI</span>
                                    <span className="emi-value">₹{loanDetails.emiAmount.toLocaleString('en-IN')}</span>
                                </div>
                                <div className={`emi-item ${lateFee ? 'highlight' : ''}`}>
                                    <span className="emi-label">Late Fee</span>
                                    <span className="emi-value">₹{lateFee.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="emi-item highlight">
                                    <span className="emi-label">Total Due</span>
                                    <span className="emi-value">₹{totalDue.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="emi-item">
                                    <span className="emi-label">Total Interest</span>
                                    <span className="emi-value">₹{loanDetails.totalInterest.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="emi-item">
                                    <span className="emi-label">Total Payable</span>
                                    <span className="emi-value">₹{loanDetails.totalPayable.toLocaleString('en-IN')}</span>
                                </div>
                            </div>
                            <div className={`late-fee-note ${lateFee ? 'late' : ''}`}>
                                {lateFee ? (
                                    <span>Late fee of ₹{lateFee.toLocaleString('en-IN')} applied because payment is after day {cutoffDay}.</span>
                                ) : (
                                    <span>Pay by day {cutoffDay} each month to avoid a ₹{lateFeeAmount.toLocaleString('en-IN')} late fee.</span>
                                )}
                            </div>
                            <div className="status-badges">
                                <span className="badge badge-success">✓ KYC Verified</span>
                                <span className="badge badge-info">Loan {loanDetails.status}</span>
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="amount">Amount (₹)</label>
                        <input
                            type="number"
                            id="amount"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            placeholder="Enter amount"
                            className="form-control"
                            min="1"
                            step="0.01"
                            required
                            disabled={formData.purpose === 'loan' && loanDetails}
                            title={formData.purpose === 'loan' && loanDetails ? 'EMI amount is auto-calculated' : ''}
                        />
                        {formData.purpose === 'loan' && loanDetails && (
                            <small style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                                EMI amount is automatically calculated based on your loan terms
                            </small>
                        )}
                    </div>

                    <div className="user-details">
                        <h3>Payment Details</h3>
                        <div className="detail-row">
                            <span className="label">Name:</span>
                            <span className="value">{user.name}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Email:</span>
                            <span className="value">{user.email}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Phone:</span>
                            <span className="value">{user.phone}</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="pay-button"
                        disabled={loading || (formData.purpose === 'loan' && !loanDetails) || validationError}
                    >
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                Pay ₹{formData.amount || '0.00'}
                            </>
                        )}
                    </button>

                    {(formData.purpose === 'loan' && !loanDetails && !validationError && formData.loanId) && (
                        <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.875rem', marginTop: '1rem' }}>
                            Enter a valid Loan ID to proceed with payment
                        </p>
                    )}

                    <div className="security-note">
                        <span className="lock-icon">🔒</span>
                        <p>Your payment is secured with 256-bit SSL encryption</p>
                    </div>
                </form>

                <div className="payment-methods">
                    <p>Accepted Payment Methods:</p>
                    <div className="methods-grid">
                        <div className="method">Credit Card</div>
                        <div className="method">Debit Card</div>
                        <div className="method">Net Banking</div>
                        <div className="method">UPI</div>
                        <div className="method">Wallets</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;
